<template>
  <div
    :class="[
      'mb-20 grid grid-cols-1 gap-16 md:grid-cols-3 lg:grid-cols-4',
      isWide && 'xl:grid-cols-5',
    ]"
  >
    <SwapBlockListing
      v-if="
        isSwapEnabled &&
        !showBatteryBlock &&
        !($slots.blockSlot1 || $slots.blockSlot2 || $slots.blockSlot3)
      "
      class="hidden h-full lg:block"
    />
    <BatteryBlock
      v-if="showBatteryBlock && batteryBlock"
      class="md:col-span-2 lg:col-span-2"
      :class="{ 'xl:col-span-2': isWide }"
      data-qa="plp-battery-block"
      :src="batteryBlock.src"
      :variant="batteryBlock.variant"
    />

    <div
      v-if="$slots.blockSlot1"
      :class="getBlockSlotClass('block1')"
      data-test="plp-block-slot-1"
    >
      <slot name="blockSlot1" />
    </div>
    <div
      v-if="$slots.blockSlot2 && products.length >= 13"
      :class="[
        getBlockSlotClass('block2'),
        isWide && 'xl:col-start-4 xl:col-end-6',
        $slots.cmsSection && products.length >= 2
          ? 'md:row-start-5 lg:row-start-4'
          : 'md:row-start-4',
      ]"
      data-test="plp-block-slot-2"
    >
      <slot name="blockSlot2" />
    </div>
    <div
      v-if="$slots.blockSlot3"
      :class="getBlockSlotClass('block3')"
      data-test="plp-block-slot-3"
    >
      <slot name="blockSlot3" />
    </div>
    <template v-if="isPending">
      <ProductCardSkeleton v-for="key in [...Array(31).keys()]" :key />
    </template>

    <template v-else>
      <div
        v-for="(product, index) in productsWithIphone17"
        :key="getItemKey(product, index)"
        :ref="
          product.isComingSoon || product.isIphone17
            ? undefined
            : setupViewportTrackingProduct(
                product,
                getProductTrackingData(product),
              )
        "
      >
        <ProductCardComingSoonProduct
          v-if="product.isComingSoon"
          :title="product.title"
          :url="product.productPageLink"
          :variant="product.comingSoonVariant ?? 'primary'"
        />
        <Iphone17ProductCard v-else-if="product.isIphone17" :product />
        <ErrorOwners
          v-else
          :owners="['bot-squad-search-and-recommendation-front']"
        >
          <ProductCard
            :id="product.id"
            :brand="product.brandClean"
            :category="product.category"
            :category-id="product.categoryId"
            :color-swatches="product.specifications?.color ?? []"
            :comparison-products
            :deals="product.deals"
            :description="product.description"
            :discounted-price="product.discountedPrice"
            :image-alt="product.title"
            :image-src="product.image"
            :is-comparison-add-product-disabled
            :is-comparison-enabled
            :is-responsive-comparison-enabled
            :listing-id="product.listingId"
            :listing-public-id="product.listingPublicId ?? ''"
            :marketing-tags="product.marketingTags"
            :model="product.modelClean"
            :price="product.price"
            :price-new="product.priceNew"
            :product
            :review-rating="product.reviewRating"
            :scores="product.scores"
            :special-offer-type="product.specialOfferType"
            :specifications="
              product.specifications
                ? Object.entries(product.specifications)
                    .map(([key, values]) => ({
                      label: key,
                      values: values.map(
                        ({ name, value }) =>
                          name + (value ? ` (${value})` : ''),
                      ),
                    }))
                    .filter(({ label }) => !excludedSpecifications.has(label))
                : []
            "
            :title="product.title"
            :url="product.productPageLink"
            :with-payment-advertisement="props.withPaymentAdvertisement"
            :with-starting-from="
              product.granularity === 'parent' &&
              product.productType !== 'Brand new'
            "
            @link-click="handleProductClick(product)"
            @toggle-comparison-product="
              emit('toggle-comparison-product', $event)
            "
          />
        </ErrorOwners>
      </div>
    </template>

    <div
      v-if="$slots.cmsSection"
      :class="[
        'md:col-span-3 lg:col-span-4',
        isWide && 'xl:col-span-5',
        { 'row-start-4 lg:row-start-2': products.length >= 2 },
      ]"
    >
      <slot name="cmsSection" />
    </div>

    <div
      v-if="
        !hideBanner &&
        !$slots.cmsSection &&
        marketingContent?.srcLarge &&
        marketingContent?.srcSmall &&
        marketingContent?.label
      "
      :ref="
        setupBannerImpressionTracker({
          name: 'landing_page',
          creative: marketingContent.label,
          position: 'landing_page_3',
        })
      "
      :class="[
        'md:col-span-3 lg:col-span-4',
        isWide && 'xl:col-span-5',
        { 'row-start-4 lg:row-start-2': products.length >= 2 },
      ]"
    >
      <MarketingCard
        :alt="marketingContent.alt"
        :label="marketingContent.label"
        :srcLarge="marketingContent.srcLarge"
        :srcSmall="marketingContent.srcSmall"
        :url="marketingContent.url"
        @click="
          trackLandingBannerClick({
            name: 'landing_page',
            creative: marketingContent.label,
            position: 'landing_page_3',
          })
        "
      />
    </div>
    <div
      v-if="article"
      :class="[
        'md:col-span-3 lg:col-span-4',
        isWide && 'xl:col-span-5',
        {
          'row-start-10 md:row-start-8 lg:row-start-5':
            products.length >= 7 &&
            !($slots.blockSlot1 || $slots.blockSlot2 || $slots.blockSlot3),
        },
      ]"
      data-test="plp-article-card"
    >
      <ArticleCard
        :id="article.id"
        :category="article.category"
        :imageAlt="article.imageAlt"
        :imageSrc="article.imageSrc"
        :link="article.link"
        :text="article.text"
        :title="article.title"
        variant="full"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRuntimeConfig, useState } from '#imports'
import { computed, toRef } from 'vue'

import { useDiscountedPrice } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useDiscountedPrice'
import type { ArticleCard as ArticleCardType } from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCard.types'
import ArticleCard from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCard.vue'
import ProductCard from '@backmarket/nuxt-layer-recommendation/ProductCard.vue'
import ProductCardComingSoonProduct from '@backmarket/nuxt-layer-recommendation/ProductCardComingSoonProduct.vue'
import ProductCardSkeleton from '@backmarket/nuxt-layer-recommendation/ProductCardSkeleton.vue'
import type { ComparisonProduct } from '@backmarket/nuxt-layer-recommendation/models/product'
import {
  type ExtraTrackingData,
  useClickTracking,
  useImpressionTracking,
} from '@backmarket/nuxt-layer-recommendation/useProductTracking'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import ErrorOwners from '@backmarket/nuxt-module-logger/components/ErrorOwners.vue'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import SwapBlockListing from '~/scopes/buyback/swap/components/ListingSwapOffer/ListingSwapOffer.vue'
import { useSwapEnabled } from '~/scopes/buyback/swap/composables/useSwapEnabled/useSwapEnabled'

import type { BatteryBlockData } from '../../composables/useBatteryBlock/useBatteryBlock'
import { useImpressionTracker } from '../../composables/useImpressionTracker'
import type {
  PlpBlockColumns,
  PlpBlockKey,
  PlpBlocksLayout,
} from '../../composables/usePlpBlocks/usePlpBlocks'
import type {
  ProductWithPosition,
  SearchTrackingData,
} from '../../composables/useProductsSearch/useProductsSearch.types'
import { COMING_SOON_INSERTION_INDEXES } from '../../constants'

import BatteryBlock from './components/BatteryBlock/BatteryBlock.vue'
import Iphone17ProductCard from './components/Iphone17ProductCard/Iphone17ProductCard.vue'
import MarketingCard from './components/MarketingCard/MarketingCard.vue'

const { trackProductClick } = useClickTracking()
const { setupViewportTrackingProduct } = useImpressionTracking()

const { trackLandingBannerImpression, trackLandingBannerClick } = useTracking()
const { setupImpressionTracker: setupBannerImpressionTracker } =
  useImpressionTracker(trackLandingBannerImpression)

const searchTrackingData = useState<SearchTrackingData>('search-query')
const emit = defineEmits(['toggle-comparison-product'])

const props = defineProps<{
  products: ProductWithPosition[]
  marketingContent?: {
    alt?: string
    label?: string
    srcSmall?: string
    srcLarge?: string
    url?: string
  }
  pageInput?: number
  article?: ArticleCardType
  reviewsCount?: number
  reviewsRate?: number
  comparisonProducts?: ComparisonProduct[]
  isComparisonEnabled?: boolean
  isComparisonAddProductDisabled?: boolean
  isResponsiveComparisonEnabled?: boolean
  isFiltered?: boolean
  withPaymentAdvertisement?: boolean
  isWide?: boolean
  batteryBlock?: BatteryBlockData
  isPending?: boolean
  list?: string
  blocksLayout?: PlpBlocksLayout
}>()

// Full class strings are required so that Tailwind can detect them.
// Only block3 supports several widths for now, other slots keep a single layout.
const BLOCK_SLOT_CLASSES: Record<
  PlpBlockKey,
  Partial<Record<PlpBlockColumns, string>>
> = {
  block1: { 1: 'row-start-2 md:row-start-1 md:col-start-1 md:col-end-2' },
  block2: {
    2: 'row-start-7 md:col-start-2 md:col-end-4 lg:col-start-3 lg:col-end-5',
  },
  block3: {
    1: 'row-start-12 md:row-start-7 md:col-start-1 md:col-end-2',
    2: 'row-start-12 md:row-start-7 md:col-start-1 md:col-end-3',
  },
}

const DEFAULT_BLOCK_COLUMNS: Record<PlpBlockKey, PlpBlockColumns> = {
  block1: 1,
  block2: 2,
  block3: 1,
}

function getBlockSlotClass(key: PlpBlockKey): string | undefined {
  const classes = BLOCK_SLOT_CLASSES[key]
  const columns = props.blocksLayout?.[key]?.columns

  return (columns && classes[columns]) ?? classes[DEFAULT_BLOCK_COLUMNS[key]]
}

const { products } = useDiscountedPrice(toRef(props, 'products'))
const { isSwapEnabled } = useSwapEnabled()
const route = useRoute()
const { market } = useMarketplace()
const {
  FF_PLP_COMING_SOON_PRODUCT_CARD = false,
  FF_PRODUCT_PAGE_IPHONE_17_EVENT_PAGE = '',
  FF_PLP_IPHONE17_UUID = '',
} = useRuntimeConfig().public

const experiments = useExperiments()
const hideBanner = experiments['experiment.hidePlpBanner'] === 'with'

const showBatteryBlock = computed(
  () =>
    Boolean(props.batteryBlock) && props.pageInput === 1 && !props.isFiltered,
)

const productsWithIphone17 = computed(() => {
  const shouldShowIphone17 =
    !FF_PLP_COMING_SOON_PRODUCT_CARD &&
    FF_PRODUCT_PAGE_IPHONE_17_EVENT_PAGE.includes(market.countryCode) &&
    FF_PLP_IPHONE17_UUID.includes(route.params.uuid as string) &&
    props.pageInput === 1 &&
    !props.isFiltered

  if (!shouldShowIphone17) return products.value

  return products.value.reduce((mergedProducts, product, index) => {
    if (COMING_SOON_INSERTION_INDEXES.includes(index)) {
      mergedProducts.push({
        id: `iphone17-${index}`,
        listingId: `iphone17-${index}`,
        position: index,
        isIphone17: true,
      } as ProductWithPosition)
    }

    mergedProducts.push(product)

    return mergedProducts
  }, [] as ProductWithPosition[])
})

const excludedSpecifications = new Set(['color', 'storage'])

function getItemKey(product: ProductWithPosition, index: number): string {
  return product.isComingSoon || product.isIphone17
    ? `${product.listingId}-${index}`
    : product.listingId
}

function getProductTrackingData(
  product: ProductWithPosition,
): ExtraTrackingData {
  return {
    provider: 'algolia',
    list: props.list ?? 'landing page product',
    position: product.position,
    source: 'search',
    queryID: searchTrackingData.value?.id,
    index: searchTrackingData.value?.index,
  }
}

function handleProductClick(product: ProductWithPosition) {
  trackProductClick(product, getProductTrackingData(product))
  searchTrackingData.value.productObjectId = product.objectId
}
</script>
